import { useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";

const OPEN_AT = 90;

export function SwipeToDelete({ tripId, tripRef, enabled, onDeleted, testId, className = "rounded-[6px]", children }) {
  const api = useApi();
  const [dx, setDx] = useState(0);
  const [drag, setDrag] = useState(false);
  const [ask, setAsk] = useState(false);
  const s = useRef(null);
  const moved = useRef(false);
  if (!enabled) return children;

  const start = (e) => {
    const t = e.touches[0];
    s.current = e.touches.length > 1 ? null : { x: t.clientX, y: t.clientY, lock: null };
    moved.current = false;
  };
  const move = (e) => {
    const p = s.current;
    if (!p) return;
    const t = e.touches[0];
    const x = t.clientX - p.x;
    const y = t.clientY - p.y;
    if (!p.lock && (Math.abs(x) > 8 || Math.abs(y) > 8)) p.lock = Math.abs(x) > Math.abs(y) * 1.3 ? "x" : "y";
    if (p.lock !== "x") return;
    moved.current = true;
    setDrag(true);
    setDx(Math.min(0, Math.max(x, -160)));
  };
  const end = () => {
    setDrag(false);
    if (s.current?.lock === "x" && dx <= -OPEN_AT) setAsk(true);
    else setDx(0);
    s.current = null;
  };
  const cancel = () => { setAsk(false); setDx(0); };
  const confirm = async () => {
    try {
      await api.delete(`/trips/${tripId}`);
      toast.success(`${tripRef} moved to the trash for 7 days`);
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e));
      cancel();
    }
  };

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid={testId}>
      <div className="absolute inset-0 flex items-center justify-end pr-6 bg-rose-600 text-white text-[0.62rem] tracking-[0.2em] uppercase font-semibold gap-2"
        style={{ opacity: dx < 0 ? 1 : 0 }} aria-hidden>
        <Trash2 size={16} /> {dx <= -OPEN_AT ? "Release to delete" : "Delete"}
      </div>
      <div onTouchStart={start} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}
        onClickCapture={(e) => { if (moved.current) { e.stopPropagation(); e.preventDefault(); moved.current = false; } }}
        style={{ transform: `translateX(${dx}px)`, transition: drag ? "none" : "transform 220ms ease" }} className="relative touch-pan-y">
        {children}
      </div>
      <AlertDialog open={ask} onOpenChange={(o) => { if (!o) cancel(); }}>
        <AlertDialogContent className="bg-[#111319] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl font-normal">Delete this trip?</AlertDialogTitle>
            <AlertDialogDescription>{tripRef} goes to the trash for 7 days, where an admin can restore it. After that it's erased with its options, photos, chat and client links.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-cancel-btn`}>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid={`${testId}-confirm-btn`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
